/**
 * Admin Service API Client
 * 
 * Handles location management, organization structure, and administrative operations
 * Port: 3006 (via API Gateway on 3001)
 * 
 * USER LOCATION MANAGEMENT APIs:
 * - getUserLocations(userId) - Get all locations assigned to a user
 * - getLocationUsers(locationId) - Get all users assigned to a location
 * - assignLocationToUser(data) - Assign a location to a user
 * - updateUserLocation(assignmentId, data) - Update location assignment
 * - removeUserLocation(assignmentId) - Remove assignment by ID
 * - removeUserLocationByIds(userId, locationId, reason) - Remove by user/location IDs
 * - setDefaultLocation(userId, locationId) - Set user's default location
 * - setPrimaryLocation(userId, locationId) - Set user's primary location
 * - getUserDefaultLocation(userId) - Get user's default location
 * - getUserPrimaryLocation(userId) - Get user's primary location
 * - bulkAssignLocations(userId, locationIds, role) - Bulk assign locations
 * - getMyLocations() - Get current user's locations
 */

import api from './api';

// ============================================
// SIGN-UP PAGE SETTINGS APIs
// ============================================

/** Protected — requires ADMIN:SIGNUP_SETTINGS:READ */
export const getSignUpPageSettings = () =>
  api.get('/admin/settings/signup-page');

/** Protected — requires ADMIN:SIGNUP_SETTINGS:UPDATE */
export const updateSignUpPageSettings = (payload) =>
  api.put('/admin/settings/signup-page', payload);

// ============================================
// LOCATION MANAGEMENT APIs
// ============================================

// Locations change rarely but are read on almost every page (report headers, location pickers), often
// several times per page. Reads are cached briefly and concurrent identical reads share one request;
// any location change clears the cache.
const LOCATION_CACHE_MS = 60 * 1000;
const locationCache = new Map(); // key → { at, promise }
const cachedLocationRead = (key, request) => {
  const hit = locationCache.get(key);
  if (hit && Date.now() - hit.at < LOCATION_CACHE_MS) return hit.promise;
  const promise = request().catch((err) => { locationCache.delete(key); throw err; });
  locationCache.set(key, { at: Date.now(), promise });
  return promise;
};
const clearLocationCache = () => locationCache.clear();

/**
 * Get all locations with optional filters
 */
const getLocations = (params = {}) => {
  return cachedLocationRead(`list:${JSON.stringify(params)}`, () => api.get('/admin/locations', { params }));
};

/**
 * Get location by ID
 */
const getLocationById = (locationId) => {
  return cachedLocationRead(`id:${locationId}`, () => api.get(`/admin/locations/${locationId}`));
};

/**
 * Create a new location
 */
const createLocation = (locationData) => {
  clearLocationCache();
  return api.post('/admin/locations', locationData).finally(clearLocationCache);
};

/**
 * Update a location
 */
const updateLocation = (locationId, locationData) => {
  clearLocationCache();
  return api.put(`/admin/locations/${locationId}`, locationData).finally(clearLocationCache);
};

/**
 * Delete a location
 */
const deleteLocation = (locationId) => {
  clearLocationCache();
  return api.delete(`/admin/locations/${locationId}`).finally(clearLocationCache);
};

/**
 * Get location statistics
 */
const getLocationStats = (locationId) => {
  return api.get(`/admin/locations/${locationId}/stats`);
};

/**
 * Get location hierarchy
 */
const getLocationHierarchy = (locationId) => {
  return api.get(`/admin/locations/${locationId}/hierarchy`);
};

/**
 * Get warehouses with capacity info
 */
const getWarehouses = (params = {}) => {
  return api.get('/admin/locations/warehouses', { params });
};

/**
 * Get warehouses with available capacity
 */
const getWarehousesWithCapacity = (minSpace, weight) => {
  return api.get('/admin/locations/warehouses/capacity', {
    params: { minSpace, weight }
  });
};

// ============================================
// USER LOCATION MANAGEMENT APIs
// ============================================

/**
 * Get locations assigned to a user
 */
const getUserLocations = (userId, params = {}) => {
  return api.get(`/admin/user-locations/users/${userId}/locations`, { params });
};

/**
 * Get users assigned to a location
 */
const getLocationUsers = (locationId) => {
  return api.get(`/admin/user-locations/locations/${locationId}/users`);
};

/**
 * Assign location to user
 */
const assignLocationToUser = (data) => {
  const { userId, locationId, ...options } = data;
  return api.post(`/admin/user-locations/users/${userId}/locations/${locationId}`, options);
};

/**
 * Update user location assignment
 */
const updateUserLocation = (assignmentId, data) => {
  return api.put(`/admin/user-locations/${assignmentId}`, data);
};

/**
 * Remove user location assignment by assignment ID
 * Note: This requires getting the assignment first, use removeUserLocationByIds for direct removal
 */
const removeUserLocation = (assignmentId) => {
  return api.delete(`/admin/user-locations/${assignmentId}`);
};

/**
 * Bulk assign locations to user
 */
const bulkAssignLocations = (userId, locations = [], role = 'user') => {
  const payload = locations.map((entry) => {
    if (typeof entry === 'string') {
      return {
        locationId: entry,
        accessLevel: 'read',
        locationRole: `location_${role}`,
      };
    }

    return {
      locationId: entry.locationId,
      accessLevel: entry.accessLevel || 'read',
      locationRole: entry.locationRole || `location_${role}`,
      ...(entry.forceUpdate ? { forceUpdate: true } : {}),
      ...(entry.notes ? { notes: entry.notes } : {}),
    };
  });

  return api.post(`/admin/user-locations/users/${userId}/locations/bulk`, {
    locations: payload,
  });
};

/**
 * Get all user-location assignments (admin only)
 */
const getAllUserLocations = (params = {}) => {
  return api.get('/admin/user-locations', { params });
};

/**
 * Get users for location assignment (basic user list)
 */
const getUsers = (params = {}) => {
  return api.get('/users', { params });
};

/**
 * Get minimal user list for CRM assignment dropdowns
 * Does not require USERS:PROFILES:READ — any authenticated user can call this
 */
const getAssignableUsers = (params = {}) => {
  return api.get('/users/assignable', { params });
};

/**
 * Get minimal role list for role-selection dropdowns (no permission required)
 */
const getAssignableRoles = () => {
  return api.get('/roles/assignable');
};

/**
 * Get current user's locations (for location switcher)
 */
const getMyLocations = () => {
  return api.get('/admin/user-locations/my-locations');
};

/**
 * Set default location for a user
 */
const setDefaultLocation = (userId, locationId) => {
  return api.post(`/admin/user-locations/users/${userId}/locations/${locationId}/set-default`);
};

/**
 * Set primary location for a user
 */
const setPrimaryLocation = (userId, locationId) => {
  return api.post(`/admin/user-locations/users/${userId}/locations/${locationId}/set-primary`);
};

/**
 * Remove user location assignment by userId and locationId
 */
const removeUserLocationByIds = (userId, locationId, reason = '') => {
  return api.delete(`/admin/user-locations/users/${userId}/locations/${locationId}`, {
    data: { reason }
  });
};

/**
 * Get user's default location
 */
const getUserDefaultLocation = (userId) => {
  return api.get(`/admin/user-locations/users/${userId}/default`);
};

/**
 * Get user's primary location
 */
const getUserPrimaryLocation = (userId) => {
  return api.get(`/admin/user-locations/users/${userId}/primary`);
};

// ============================================
// LOCATION SEARCH & FILTERS
// ============================================

/**
 * Search locations by name or code
 */
const searchLocations = (query) => {
  return api.get('/admin/locations/search', { params: { query } });
};

/**
 * Get active locations
 */
const getActiveLocations = (type = null) => {
  const params = type ? { type } : {};
  return api.get('/admin/locations/active', { params });
};

/**
 * Get locations by type
 */
const getLocationsByType = (type) => {
  return api.get('/admin/locations', { params: { type } });
};

/**
 * Get locations by city
 */
const getLocationsByCity = (city) => {
  return api.get('/admin/locations', { params: { city } });
};

// ============================================
// EXPORT
// ============================================

export const adminAPI = {
  // Locations
  getLocations,
  getLocationById,
  createLocation,
  updateLocation,
  deleteLocation,
  getLocationStats,
  getLocationHierarchy,
  getWarehouses,
  getWarehousesWithCapacity,
  searchLocations,
  getActiveLocations,
  getLocationsByType,
  getLocationsByCity,
  
  // User Locations
  getUserLocations,
  getLocationUsers,
  getAllUserLocations,
  getUsers,
  getAssignableUsers,
  getAssignableRoles,
  assignLocationToUser,
  updateUserLocation,
  removeUserLocation,
  removeUserLocationByIds,
  bulkAssignLocations,
  getMyLocations,
  setDefaultLocation,
  setPrimaryLocation,
  getUserDefaultLocation,
  getUserPrimaryLocation
};

export const reportsApi = {
  getAuditLogs: (params) => api.get('/admin/reports/audit-logs', { params }),
  exportAuditLogs: (payload) =>
    api.post('/admin/reports/audit-logs/export', payload, { responseType: 'blob' }),
};

export default adminAPI;
